import useToast from '@/hooks/useToast';
import FolderDetailLayout from 'layouts/FolderDetailLayout/FolderDetailLayout';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { IoWarningOutline } from 'react-icons/io5';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { hardDeleteFile } from '../../apis/tms/file.api';
import {
  hardDeleteFolder,
  middlewarePostSearchFolder,
} from '../../apis/tms/folder.api';
import { getExistingFolder } from '../../apis/tms/my-space.api';
import { getFoldersShared, leaveFolderOrGroups } from '../../apis/tms/share';
import AddNewGroupButton from '../../components/buttons/training-module/AddNewGroupButton';
import EmptyScreen from '../../components/elements/EmptyScreen';
import EmptySeach from '../../components/elements/EmptySearch';
import FolderPreviewGallery from '../../components/gallery/FolderPreviewGallery';
import RightClick from '../../components/menu/RightClick.menu';
import { PATH_NAME } from '../../configs/pathName';
import {
  FOLDER_LIST_MY_SPACE,
  FOLDER_LIST_SHARE_WITH_ME,
} from '../../constant/QueryKeyName';
import { SEARCH_QUERY } from '../../constant/queryURLKey';
import { ROLE_ACCESS } from '../../constant/storage';
import { PlaceSearchEnum } from '../../interfaces/training.interface';
import AuthContext from '../../state/auth/AuthContext';

import { generateQuery } from '@/helpers/pathName';
import dynamic from 'next/dynamic';

const Accessibility = dynamic(
  () => import('../../components/modal/Accessibility.modal')
);
const ConfirmModal = dynamic(
  () => import('../../components/modal/training-module/ConfirmModal')
);
const ModifyGroupModal = dynamic(
  () => import('../../components/modal/training-module/ModifyGroupModal')
);

const MyGroups = () => {
  const [openAccessibilityModal, setOpenAccessibilityModal] =
    useState<boolean>(false);
  const [openModifyGroupModal, setOpenModifyGroupModal] =
    useState<boolean>(false);
  const [folderNeedEdit, setFolderNeedEdit] = useState<
    undefined | { [key: string]: any }
  >(undefined);

  const [confirmFolderModalOpen, setConfirmFolderModalOpen] = useState<
    undefined | { item: any; queryKey: string | string[] }
  >(undefined);

  const [empty, setEmpty] = useState<boolean>(false);

  const [searchValue, setSearchValue] = useState('');

  const queryClient = useQueryClient();

  const toast = useToast();

  const handleHardDelete = async (
    type: 'file' | 'folder',
    id: string,
    queryKey: string | string[]
  ) => {
    try {
      type === 'file' ? await hardDeleteFile(id) : await hardDeleteFolder(id);
      queryClient.refetchQueries(queryKey);
      toast(`Xoá ${type === 'folder' ? 'nhóm' : 'tệp'} thành công`, 'success');
    } catch (error) {
      toast(`Xoá ${type === 'folder' ? 'nhóm' : 'tệp'} thất bại`, 'error');
    }
  };

  const handleLeaveGroup = async (id: string, queryKey: string | string[]) => {
    try {
      await leaveFolderOrGroups({ id });
      queryClient.refetchQueries(queryKey);
      router.replace(PATH_NAME.MY_GROUPS);
      toast(`Rời nhóm thành công`, 'success');
    } catch (error) {
      toast(`Rời nhóm thất bại`, 'error');
    }
  };

  const router = useRouter();

  const { isLoggedIn } = useContext(AuthContext);

  useEffect(() => {
    !isLoggedIn() &&
      router.push({
        pathname: PATH_NAME.LOGIN,
        query: generateQuery(router),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (router.isReady) {
      setSearchValue(router.query[SEARCH_QUERY] as string);
    }
  }, [router.isReady, router.query]);

  const { data: folderData } = useInfiniteQuery(
    !searchValue
      ? [FOLDER_LIST_MY_SPACE]
      : [FOLDER_LIST_MY_SPACE, searchValue, PlaceSearchEnum.Dashboard],
    !searchValue ? getExistingFolder : middlewarePostSearchFolder,
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  const { data: sharedFolderData } = useInfiniteQuery(
    !searchValue
      ? [FOLDER_LIST_SHARE_WITH_ME]
      : [FOLDER_LIST_MY_SPACE, searchValue, PlaceSearchEnum.Shared],
    !searchValue ? getExistingFolder : middlewarePostSearchFolder,
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (
      folderData?.pages[0]?.data?.length === 0 &&
      sharedFolderData?.pages[0]?.data?.length === 0
    ) {
      setEmpty(true);
    } else {
      setEmpty(false);
    }
  }, [folderData, sharedFolderData]);

  return (
    <>
      <FolderDetailLayout>
        <div className="mx-auto flex w-full max-w-container flex-col gap-10">
          {empty ? (
            <>
              {!searchValue ? (
                <EmptyScreen
                  description="Bạn chưa tham gia vào nhóm nào."
                  addNew={
                    <AddNewGroupButton queryKey={[FOLDER_LIST_MY_SPACE]} />
                  }
                />
              ) : (
                <EmptySeach description="Thử lại nhé" />
              )}
            </>
          ) : (
            <>
              <section className="mt-10">
                <div className="mb-2 ml-auto w-fit">
                  <AddNewGroupButton queryKey={[FOLDER_LIST_MY_SPACE]} />
                </div>

                <FolderPreviewGallery
                  groups
                  handleClickItem={() => {}}
                  title={'Nhóm của tôi'}
                  titleClassName="bg-primary_supa_lighter/30"
                  rightClick={
                    <RightClick
                      menuType="folder"
                      previewCallback={(_currenItem: any) => () =>
                        router.push(
                          `${PATH_NAME.MY_GROUPS}/${_currenItem?.id}`
                        )}
                      shareCallback={(_currenItem: any) => () => {
                        setFolderNeedEdit(_currenItem);
                        setOpenAccessibilityModal(true);
                      }}
                      editCallback={(_currenItem: any) => () => {
                        setFolderNeedEdit(_currenItem);
                        setOpenModifyGroupModal(true);
                      }}
                      removeFromFolderCallback={() => () => console.log('test')}
                      moveToTrashCallback={(_currenItem: any) => () =>
                        setConfirmFolderModalOpen({
                          item: _currenItem,
                          queryKey: [FOLDER_LIST_MY_SPACE],
                        })}
                      moveToTrashContent="Xóa nhóm"
                    />
                  }
                  loading={false}
                  fetchDataFunc={
                    !searchValue
                      ? getExistingFolder
                      : middlewarePostSearchFolder
                  }
                  queryKey={
                    !searchValue
                      ? [FOLDER_LIST_MY_SPACE]
                      : [
                          FOLDER_LIST_MY_SPACE,
                          searchValue,
                          PlaceSearchEnum.Dashboard,
                        ]
                  }
                />
              </section>
              <section>
                <FolderPreviewGallery
                  groups
                  handleClickItem={() => {}}
                  title={'Đã tham gia'}
                  titleClassName="bg-[#dfeed8]"
                  rightClick={
                    <RightClick
                      menuType="folder"
                      previewCallback={(_currenItem: any) => () =>
                        router.push(
                          `${PATH_NAME.MY_GROUPS}/${_currenItem?.id}`
                        )}
                      shareCallback={(_currenItem: any) =>
                        _currenItem.currentUserPermission === ROLE_ACCESS.Editor
                          ? () => {
                              setFolderNeedEdit(_currenItem);
                              setOpenAccessibilityModal(true);
                            }
                          : undefined
                      }
                      // editCallback={(_currenItem: any) => () => {
                      //   setFolderNeedEdit(_currenItem);
                      //   setOpenModifyGroupModal(true);
                      // }}
                      removeFromFolderCallback={() => () => console.log('test')}
                      moveToTrashCallback={(_currenItem: any) => () =>
                        handleLeaveGroup(_currenItem.id, [
                          FOLDER_LIST_SHARE_WITH_ME,
                        ])}
                      moveToTrashContent="Rời nhóm"
                    />
                  }
                  loading={false}
                  fetchDataFunc={
                    !searchValue ? getFoldersShared : middlewarePostSearchFolder
                  }
                  queryKey={
                    !searchValue
                      ? [FOLDER_LIST_SHARE_WITH_ME]
                      : [
                          FOLDER_LIST_MY_SPACE,
                          searchValue,
                          PlaceSearchEnum.Shared,
                        ]
                  }
                />
              </section>
            </>
          )}
        </div>
      </FolderDetailLayout>

      {openAccessibilityModal && (
        <Accessibility
          isVisible={openAccessibilityModal}
          toggleModal={() => setOpenAccessibilityModal(false)}
          folderId={folderNeedEdit?.id}
        />
      )}
      {openModifyGroupModal && (
        <ModifyGroupModal
          open={openModifyGroupModal}
          setOpen={() => setOpenModifyGroupModal(false)}
          defaultData={folderNeedEdit}
          queryKey={[FOLDER_LIST_MY_SPACE]}
        />
      )}
      {confirmFolderModalOpen !== undefined && (
        <ConfirmModal
          color="secondary"
          title="Xóa nhóm"
          description="Toàn bộ nội dung trong nhóm sẽ bị xóa vĩnh viễn. Hành động này không thể hoàn tác. Bạn chắc chứ?"
          cancelContent="Hủy"
          confirmContent={
            <div className="flex items-center gap-x-2">
              <IoWarningOutline className="text-lg" />
              <p>Xóa nhóm vĩnh viễn</p>
            </div>
          }
          open={confirmFolderModalOpen !== undefined}
          onClose={() => setConfirmFolderModalOpen(undefined)}
          cancelCallback={() => setConfirmFolderModalOpen(undefined)}
          confirmCallback={() =>
            handleHardDelete(
              'folder',
              confirmFolderModalOpen?.item?.id || '',
              confirmFolderModalOpen?.queryKey || ''
            )
          }
        />
      )}
    </>
  );
};

export default MyGroups;
